import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, Injector, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {audit, startWith, takeUntil, tap} from 'rxjs/operators';
import {
    ValueWithDelDisplayDecimalPipe
} from '../../../components/bigint-display-decimal-pipe/value-with-del-display-decimal.pipe';
import {CustomScrollbarModule} from '../../../components/custom-scrollbar';
import {destroy$} from '../../../components/destroyable';
import {DexComputedLpService} from '../../../components/dex/dex-streaming/computed/dex-computed-lp.service';
import {ComputedHeldPosition} from '../../../components/dex/dex-streaming/types/computed/computed-held-position';
import {WalletAvatarComponent} from '../../../components/dex/wallet-avatar/wallet-avatar.component';
import {DisplayDecimalModule} from '../../../components/display-decimal';
import {ShortenWalletAddrPipe} from '../../../components/dynamic-xyz/utils/shorten-wallet-addr.pipe';
import {ElementRulerModule} from '../../../components/element-ruler';
import {WIDGET_UPDATES_GATING$} from '../../../components/layout/widget/widget-updates-gating';
import {TablesModule} from '../../../components/tables';
import {
    getDefaultColumnsConfigByDef,
    normalizeColumnsConfig,
    TableColumnsSizingMode
} from '../../../components/tables/table';
import {TooltipModule} from '../../../components/tooltip';
import {L10nModule} from '../../../components/translate';
import {TextHidingModule} from '../../../directives/text-hiding';
import {LP_MOCK} from '../../dex-lp-widget/tranches-mock';
import {
    DEX_LP_DETAILS_TABLE_COLUMNS_DICT,
    DEX_LP_DETAILS_TABLE_COLUMNS_SIZING,
    DEX_LP_TABLE_COLUMNS_DEF,
    DexDisplayLpDetailsComposItem,
    DexLpDetailsTable,
    DexLpDetailsTableColumnConfig
} from './dex-lp-details-table';

const EMPTY_ROW = {} as DexDisplayLpDetailsComposItem;

@Component({
    selector:        'dex-lp-details-table',
    templateUrl:     './dex-lp-details-table.component.html',
    styleUrl:        './dex-lp-details-table.component.scss',
    imports: [
        CustomScrollbarModule,
        ElementRulerModule,
        L10nModule,
        TablesModule,
        TextHidingModule,
        TooltipModule,
        ShortenWalletAddrPipe,
        DisplayDecimalModule,
        WalletAvatarComponent,
        ValueWithDelDisplayDecimalPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DexLpDetailsTableComponent implements OnInit {

    public readonly table: DexLpDetailsTable = {
        columnsDict:        DEX_LP_DETAILS_TABLE_COLUMNS_DICT,
        columnsConfig:      normalizeColumnsConfig(
            DEX_LP_TABLE_COLUMNS_DEF,
            getDefaultColumnsConfigByDef(DEX_LP_TABLE_COLUMNS_DEF)
        ),
        columnsSizingSteps: DEX_LP_DETAILS_TABLE_COLUMNS_SIZING,
        columnsSizingMode:  TableColumnsSizingMode.AUTO,
        rows:               [],
        emptyRow:           EMPTY_ROW,
        columnsUpdate$:     new Subject<DexLpDetailsTableColumnConfig[]>()
    };

    private readonly $lp = computed(() => this.dexComputedLpService.$list()[0]);

    private readonly triggerUpdateRows$ = new Subject<ComputedHeldPosition[]>();

    private readonly destroy$ = destroy$();

    constructor(
        private readonly injector: Injector,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly dexComputedLpService: DexComputedLpService
    ) {}

    public getTypedRow(row: DexDisplayLpDetailsComposItem): DexDisplayLpDetailsComposItem {
        return row;
    }

    public ngOnInit(): void {
        effect(() => {
            this.triggerUpdateRows$.next(this.$lp()?.$computedPositions() || []);
        }, {injector: this.injector});

        this.initRowsUpdating();
    }

    private initRowsUpdating(): void {
        this.triggerUpdateRows$.pipe(
            startWith(this.$lp()?.$computedPositions() || []),
            audit(() => WIDGET_UPDATES_GATING$),
            tap(ps => this.updateRows(ps)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateRows(positions: ComputedHeldPosition[]): void {
        const mockRows = LP_MOCK.composition;

        this.table.rows = positions.map((pos, iPos) => {
            const mockRow = mockRows[iPos % mockRows.length];

            return {
                raw:                  pos,
                id:                   mockRow.id,
                symbol:               pos.deliverable?.symbol || `#${pos.raw.identifier.deliverableId}`,
                unrealisedPnlPercent: pos.yieldPercent,
                counterparty:         mockRow.counterparty
            } satisfies DexDisplayLpDetailsComposItem;
        });

        this.changeDetectorRef.markForCheck();
    }

}
